import { QueryClient } from '@tanstack/react-query';
import { persistQueryClient } from '@tanstack/query-persist-client-core';
import { persister } from './queryPersister';

// Create a client with optimized defaults for performance and offline support
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // OPTIMIZED: Increased from 5min to 10min for better caching
      staleTime: 10 * 60 * 1000,
      // Keep cache for 24 hours (for offline support)
      gcTime: 24 * 60 * 60 * 1000,
      // Retry failed requests with exponential backoff
      retry: (failureCount, error: any) => {
        // Don't retry on 404s or 401s
        if (error?.status === 404 || error?.status === 401) return false;
        // Retry up to 3 times
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Keep showing old data while fetching new
      placeholderData: (previousData: any) => previousData,
      // OPTIMIZED: Disable refetch on window focus to prevent form state loss
      // This prevents components from remounting when switching browser tabs
      refetchOnWindowFocus: false,
      // OPTIMIZED: Only refetch on reconnect if stale (was 'always')
      refetchOnReconnect: (query) => {
        return query.state.isInvalidated || query.isStale();
      },
      // Network-first strategy with offline fallback
      networkMode: 'offlineFirst',
    },
  },
});

// Persist the client for offline support
persistQueryClient({
  queryClient,
  persister,
  maxAge: 24 * 60 * 60 * 1000, // 24 hours
  hydrateOptions: {},
  dehydrateOptions: {
    shouldDehydrateQuery: (query) => {
      // Only persist successful queries
      return query.state.status === 'success';
    },
  },
});

// Cache keys for consistent query management
export const queryKeys = {
  user: (email: string) => ['user', email] as const,
  creators: () => ['creators'] as const,
  picks: {
    all: () => ['picks'] as const,
    byUser: (userId: string) => ['picks', 'user', userId] as const,
    subscribed: (userId: string) => ['picks', 'subscribed', userId] as const,
    detail: (pickId: string) => ['picks', 'detail', pickId] as const,
  },
  purchases: {
    byUser: (userId: string) => ['purchases', 'user', userId] as const,
    byCreator: (creatorId: string) => ['purchases', 'creator', creatorId] as const,
  },
  analytics: (userId: string, timeFrame: string) => ['analytics', userId, timeFrame] as const,
};